import React from 'react';

import style from './AIGenerationDashboard.module.css';

import CustomIcon from 'Crow/Form/Field/Custom/Common/CustomIcon/CustomIcon.js';

import {
	ListingConfigContext,
} from './ListingConfigContext.js';

import {
	pollAIGenerationEventsByAccount,
	pollAIGenerationEventsByListingColumn,
	pollAIGenerationEventsByListingConfig,
} from 'Vulture/AIGeneration';

class AIGenerationStatusView extends React.Component {
	static contextType = ListingConfigContext;

	constructor(props) {
		super(props);
		this.state = {
			loading:           false,
			error:             null,
			events:            [],
			mode:              'all_listings', // 'all_listings' | 'gallery' | 'listing_column' | 'listing_config'
			listingColumnUUID: '',
			listingConfigUUID: '',
			inputError:        null,
			lastRefreshTime:   null,
		};

		this.pollInterval = null;
	}

	componentDidMount() {
		this.startPolling();
	}

	componentWillUnmount() {
		if (this.pollInterval) {
			clearInterval(this.pollInterval);
		}
	}

	startPolling = () => {
		this.pollEvents(); // initial fetch
		if (this.pollInterval) {
			clearInterval(this.pollInterval);
		}
		this.pollInterval = setInterval(this.pollEvents, 60000);
	};

	pollEvents = () => {
		const {
			mode,
		} = this.state;
		// 'all_listings' and 'gallery' both poll the account endpoint (which
		// returns both domains' events) and filter client-side by mode.
		if (mode === 'all_listings' || mode === 'gallery') {
			this.fetchEventsByAccount();
		} else if (mode === 'listing_column') {
			this.fetchEventsByListingColumn();
		} else if (mode === 'listing_config') {
			this.fetchEventsByListingConfig();
		}
	};

	// The account poll returns both listing and gallery events. Listing events
	// carry listing_uuid; gallery events carry gallery_file_id — so the tab
	// filters on which key is present.
	filterEventsByMode = (events) => {
		const list = Array.isArray(events) ? events : [];
		const { mode } = this.state;
		if (mode === 'gallery') {
			return list.filter((event) => event.gallery_file_id != null);
		}
		if (mode === 'all_listings') {
			return list.filter((event) => event.listing_uuid != null);
		}
		return list;
	};

	handleModeChange = (e) => {
		const mode = e.target.value;
		this.setState({ mode, error: null, inputError: null, events: [] }, () => {
			this.startPolling();
		});
	};

	handleInputChange = (e) => {
		this.setState({ [e.target.name]: e.target.value, inputError: null });
	};

	fetchEventsByAccount = async () => {
		const { accountUUID } = this.props;
		this.setState({ loading: true, error: null, events: [] });
		try {
			const data = await pollAIGenerationEventsByAccount(accountUUID);
			this.setState({ events: this.filterEventsByMode(data.events), loading: false, lastRefreshTime: new Date() });
		} catch (error) {
			this.setState({ error: error.message || 'Failed to fetch events', loading: false, lastRefreshTime: new Date() });
		}
	};

	fetchEventsByListingColumn = async () => {
		const { listingColumnUUID } = this.state;
		if (!listingColumnUUID) {
			this.setState({ inputError: 'Listing Column UUID required.' });
			return;
		}
		this.setState({ loading: true, error: null, events: [] });
		try {
			const data = await pollAIGenerationEventsByListingColumn(listingColumnUUID);
			this.setState({ events: data.events, loading: false, lastRefreshTime: new Date() });
		} catch (error) {
			this.setState({ error: error.message || 'Failed to fetch events', loading: false, lastRefreshTime: new Date() });
		}
	};

	fetchEventsByListingConfig = async () => {
		const { listingConfigUUID } = this.state;
		if (!listingConfigUUID) {
			this.setState({ inputError: 'Listing Config UUID required.' });
			return;
		}
		this.setState({ loading: true, error: null, events: [] });
		try {
			const data = await pollAIGenerationEventsByListingConfig(listingConfigUUID);
			this.setState({ events: data.events, loading: false, lastRefreshTime: new Date() });
		} catch (error) {
			this.setState({ error: error.message || 'Failed to fetch events', loading: false, lastRefreshTime: new Date() });
		}
	};

	handleFetch = (e) => {
		e.preventDefault();
		const { mode } = this.state;
		if (mode === 'listing_column') {
			this.fetchEventsByListingColumn();
		} else if (mode === 'listing_config') {
			this.fetchEventsByListingConfig();
		}
	};

	// Status icon renderer for table
	renderStatusIcon = (status) => {
		if (status === 'pending') {
			return <CustomIcon icon="hourglass-half" color="#888" size="lg" title="Pending" />;
		}
		if (status === 'processed') {
			return <div className={style.spinner}><CustomIcon icon="spinner" color="#1890ff" size="lg" title="Processed" /></div>;
		}
		if (status === 'finished') {
			return <CustomIcon icon="check-circle" color="green" size="lg" title="Completed" />;
		}
		if (status === 'failed') {
			return <CustomIcon icon="times-circle" color="red" size="lg" title="Error" />;
		}
		return status || '-';
	};

	// Gallery events carry their output as a JSON string in generated_value
	// ({"keywords": [...]}). Parse it for display; tolerate malformed/empty
	// values (older events may carry extra keys — ignored).
	parseGalleryOutput = (generatedValue) => {
		if (!generatedValue) {
			return { keywords: '' };
		}
		try {
			const parsed = JSON.parse(generatedValue);
			const keywords = Array.isArray(parsed.keywords) ? parsed.keywords.join(', ') : (parsed.keywords || '');
			return { keywords };
		} catch (e) {
			return { keywords: '' };
		}
	};

	renderListingTable = (events) => (
		<table className={style.table}>
			<thead>
				<tr>
					<th className={style.tableHeaderCell}>Event ID</th>
					<th className={style.tableHeaderCell}>Status</th>
					<th className={style.tableHeaderCell}>Attempts</th>
					<th className={style.tableHeaderCell}>Tokens Used</th>
					<th className={style.tableHeaderCell}>Processing Time (ms)</th>
					<th className={style.tableHeaderCell}>Error Message</th>
					<th className={style.tableHeaderCell}>Generated Value</th>
					<th className={style.tableHeaderCell}>Completed At</th>
				</tr>
			</thead>
			<tbody>
				{[...events].sort((a, b) => (b.event_id || 0) - (a.event_id || 0)).map((event) => (
					<tr key={event.event_id}>
						<td className={style.tableCell}>{event.event_id || '-'}</td>
						<td className={style.tableCell}>{this.renderStatusIcon(event.status)}</td>
						<td className={style.tableCell}>{event.attempt_count || '-'}</td>
						<td className={style.tableCell}>{event.tokens_used || '-'}</td>
						<td className={style.tableCell}>{event.processing_time_ms || '-'}</td>
						<td className={style.tableCell}>{event.error_message || '-'}</td>
						<td className={style.tableCell}>{event.generated_value || '-'}</td>
						<td className={style.tableCell}>{event.completed_at ? new Date(event.completed_at).toLocaleString() : '-'}</td>
					</tr>
				))}
			</tbody>
		</table>
	);

	renderGalleryTable = (events) => (
		<table className={style.table}>
			<thead>
				<tr>
					<th className={style.tableHeaderCell}>Event ID</th>
					<th className={style.tableHeaderCell}>Status</th>
					<th className={style.tableHeaderCell}>Attempts</th>
					<th className={style.tableHeaderCell}>Asset Type</th>
					<th className={style.tableHeaderCell}>Keywords</th>
					<th className={style.tableHeaderCell}>Tokens Used</th>
					<th className={style.tableHeaderCell}>Processing Time (ms)</th>
					<th className={style.tableHeaderCell}>Error Message</th>
					<th className={style.tableHeaderCell}>Completed At</th>
				</tr>
			</thead>
			<tbody>
				{[...events].sort((a, b) => (b.event_id || 0) - (a.event_id || 0)).map((event) => {
					const { keywords } = this.parseGalleryOutput(event.generated_value);
					return (
						<tr key={event.event_id}>
							<td className={style.tableCell}>{event.event_id || '-'}</td>
							<td className={style.tableCell}>{this.renderStatusIcon(event.status)}</td>
							<td className={style.tableCell}>{event.attempt_count || '-'}</td>
							<td className={style.tableCell}>{event.asset_type || '-'}</td>
							<td className={style.tableCell}>{keywords || '-'}</td>
							<td className={style.tableCell}>{event.tokens_used || '-'}</td>
							<td className={style.tableCell}>{event.processing_time_ms || '-'}</td>
							<td className={style.tableCell}>{event.error_message || '-'}</td>
							<td className={style.tableCell}>{event.completed_at ? new Date(event.completed_at).toLocaleString() : '-'}</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);

	render() {
		const { loading, error, events, mode, listingColumnUUID, listingConfigUUID, inputError, lastRefreshTime } = this.state;

		const { listingConfigMap, } = this.context;

		return (
			<div className={style.sectionContainer}>
				<div className={style.sectionHeader}>
					<h3 className={style.sectionTitle}>AI Generation Status</h3>
				</div>
				<div className={style.sectionContent}>
					<div className={style.tabContainer}>
						<div className={style.tabList}>
							{[
								{ label: 'All Listings', value: 'all_listings' },
								{ label: 'Listing Column', value: 'listing_column' },
								{ label: 'Listing Config', value: 'listing_config' },
								{ label: 'Gallery', value: 'gallery' }
							].map((tab) => (
								<div
									key={tab.value}
									onClick={() => this.handleModeChange({ target: { value: tab.value } })}
									className={`${style.tab} ${mode === tab.value ? style.tabActive : ''}`}
								>
									{tab.label}
								</div>
							))}
						</div>
					</div>
					{mode === 'listing_column' && (
						(() => {
							// Parse listing_column uuids with active ai_generation_config
							const columnOptions = [];
							Object.values(listingConfigMap || {}).forEach((value) => {
								Object.entries(value || {}).forEach(([ colKey, colValue, ]) => {
									if (
										colKey.startsWith('listing_column.') &&
										colValue &&
										colValue.ai_generation_config &&
										colValue.ai_generation_config.is_active === true
									) {
										const columnUUID = colKey.split('.')[1];
										columnOptions.push({
											uuid:        columnUUID,
											displayName: colValue.ai_generation_config.prompt_template || columnUUID,
										});
									}
								});
							});
							// Remove duplicates by uuid
							const uniqueOptions = Array.from(
								new Map(columnOptions.map((opt) => [opt.uuid, opt])).values()
							);
							return (
								<form onSubmit={this.handleFetch} className={style.formContainer}>
									<label htmlFor="listingColumnUUID" className={style.formLabel}>Select Listing Column:</label>
									<select
										id="listingColumnUUID"
										name="listingColumnUUID"
										value={listingColumnUUID}
										onChange={this.handleInputChange}
										className={style.formSelect}
									>
										<option value="">-- Select --</option>
										{uniqueOptions.map(opt => (
											<option key={opt.uuid} value={opt.uuid}>{opt.displayName}</option>
										))}
									</select>
									<button type="submit">Fetch Events</button>
									{inputError && <span className={style.errorText}>{inputError}</span>}
								</form>
							);
						})()
					)}
					{mode === 'listing_config' && (
						(() => {
							// Parse listing_config uuids with active ai_generation_config
							const configOptions = [];
							Object.entries(listingConfigMap).forEach(([ key, value, ]) => {
								let hasActive = false;
								Object.entries(value).forEach(([ colKey, colValue, ]) => {
									if (
										colKey.startsWith('listing_column.') &&
										colValue &&
										colValue.ai_generation_config &&
										colValue.ai_generation_config.is_active === true
									) {
										hasActive = true;
									}
								});
								if (hasActive) {
									configOptions.push({
										uuid:        key,
										displayName: value.display_name || key,
									});
								}
							});
							// Remove duplicates by uuid
							const uniqueOptions = Array.from(
								new Map(configOptions.map(opt => [opt.uuid, opt])).values()
							);
							return (
								<form onSubmit={this.handleFetch} className={style.formContainer}>
									<label htmlFor="listingConfigUUID" className={style.formLabel}>Select Listing Config:</label>
									<select
										id="listingConfigUUID"
										name="listingConfigUUID"
										value={listingConfigUUID}
										onChange={this.handleInputChange}
										className={style.formSelect}
									>
										<option value="">-- Select --</option>
										{uniqueOptions.map(opt => (
											<option key={opt.uuid} value={opt.uuid}>{opt.displayName}</option>
										))}
									</select>
									<button type="submit">Fetch Events</button>
									{inputError && <span className={style.errorText}>{inputError}</span>}
								</form>
							);
						})()
					)}
					{loading && <div>Loading...</div>}
					{error && <div className={style.errorMessage}>{error}</div>}
					{!loading && !error && (
						<div>
							<div className={style.refreshContainer}>
								{loading && <span>Refreshing...</span>}
								{lastRefreshTime && (
									<span>Last refresh: {lastRefreshTime.toLocaleTimeString()}</span>
								)}
								<div
									onClick={this.pollEvents}
									className={style.refreshIcon}
								>
									<CustomIcon 
										icon="sync-alt" 
										color="#007bff" 
										size="sm" 
										title="Refresh now"
									/>
								</div>
							</div>
							{Array.isArray(events) && events.length === 0 && <div>No AI generation events found.</div>}
							{Array.isArray(events) && events.length > 0 && (
								mode === 'gallery' ? this.renderGalleryTable(events) : this.renderListingTable(events)
							)}
						</div>
					)}
				</div>
			</div>
		);
	}
}

export default AIGenerationStatusView;